import React, { useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import './Auth.css';
import Footer from '../../Footer';
import PasswordField from './PasswordField';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // This page has two arrivals. The emailed "Open the reset page" CTA is a fresh
  // document load at /password/reset?email=&code=, where location.state is always
  // null. The in-app Link from ForgotPassword.js carries location.state and has
  // no query string. Seed from whichever one this arrival supplied.
  //
  // These are useState initializers, so they run on the first render only - the
  // render that already has this arrival's query string. Later renders must not
  // re-read the URL, or they would overwrite whatever the user has typed.
  const [email, setEmail] = useState(
    () => searchParams.get('email') || location.state?.email || ''
  );
  const [resetCode, setResetCode] = useState(() => searchParams.get('code') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post('/api/auth/update-password', {
        email,
        password,
        resetCode,
      });

      setStatusMessage('Password updated successfully. Redirecting to login...');
      navigate('/login', {
        replace: true,
        state: {
          successNotice: 'Your new password is set. Sign in to keep going.',
        },
      });
    } catch (error) {
      const friendlyMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'We could not update your password. Please try again.';

      setErrorMessage(friendlyMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="auth-page auth-page--narrow">
        <span className="deco deco--2 auth-deco--tl" aria-hidden="true" />
        <span className="deco deco--1 auth-deco--br" aria-hidden="true" />
        <div className="auth-inner auth-inner--narrow">
          <header className="auth-header">
            <span className="eyebrow">Reset your password</span>
            <h1>Secure your account</h1>
            <p className="sub">
              Enter the code we sent to your email along with your new password. You&apos;ll be ready to log back in
              immediately.
            </p>
          </header>

          <section className="auth-form">
            <form className="auth-card" onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="reset-email">Email address</label>
                  <div className="input-shell">
                    <input
                      id="reset-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                  </div>
                  <p className="helper-text">Use the email that received your reset code.</p>
                </div>

                <div className="form-group">
                  <label htmlFor="reset-code">Reset code</label>
                  <div className="input-shell">
                    <input
                      id="reset-code"
                      type="text"
                      placeholder="Enter the code from your email"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value)}
                      autoComplete="one-time-code"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="new-password">New password</label>
                  <PasswordField
                    id="new-password"
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={6}
                    autoComplete="new-password"
                    required
                  />
                  <p className="helper-text">Passwords must be at least 6 characters.</p>
                </div>

                <div className="form-group">
                  <label htmlFor="confirm-password">Confirm new password</label>
                  <PasswordField
                    id="confirm-password"
                    placeholder="Re-enter your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={6}
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>

              {statusMessage && <p className="helper-text success">{statusMessage}</p>}
              {errorMessage && <p className="helper-text error">{errorMessage}</p>}

              <button className="auth-submit" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Updating password...' : 'Reset password'}
              </button>

              <p className="form-footer-note">
                Haven&apos;t received a code?{' '}
                <Link to="/password/forgot" className="auth-link">
                  Request a new one
                </Link>
                .
              </p>
            </form>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ResetPassword;
